import React, { FC, useEffect, useState } from 'react'
import { hot } from 'react-hot-loader/root'
import { observer } from 'mobx-react'
import { DWApplyCloudGameConnect, DWApplyCloudGameReport } from 'src/commons/store/CloudGameStore'
import { WindowSettingsModal, TimeShow, Config, InitConfig } from './components'
import { getCloudInfoHS } from 'src/commons/store/GameStore'
import { CLOUD_GAME_EXIT_TYPE } from 'src/pc/store/cloudGame/cloudGameHelper'
import classnames from 'classnames'
import styled from '@emotion/styled'
import CloudGamePagePostMessage from './cloudGamePagePostMessage'

import RectImg from 'src/pc/assets/images/cloudGame/rect.png'
import ArrowRight from 'src/pc/assets/images/cloudGame/arrow-right.png'
import TimeImg from 'src/pc/assets/images/cloudGame/time.png'
import ScreenSetImg from 'src/pc/assets/images/cloudGame/screen-set.png'
import ScreenFullImg from 'src/pc/assets/images/cloudGame/screen-full.png'
import ExitScreenFullImg from 'src/pc/assets/images/cloudGame/exitfullscreen.png'
import ExitImg from 'src/pc/assets/images/cloudGame/exit.png'
import InviteImg from 'src/pc/assets/images/cloudGame/invite.png'
import RecoverImg from 'src/pc/assets/images/cloudGame/recover.png'
interface PageProps {
  query: Record<string, string>
  env: string
}

let timer: number | null = null
let animationId: number | null = null
let hangSuccess: Function | null = null

const PlayCloudGame: FC<PageProps> = ({ query, env }) => {
  const [visibleConfigModal, setVisibleConfigModal] = useState<boolean>(false) // 分辨率设置弹窗
  const [config, setConfig] = useState<Config>(InitConfig)
  const [controlCollapse, setControlCollapse] = useState<boolean>(true) // 游戏配置工具栏
  const [networkStatus, setNetWorkStatus] = useState<OnNetworkChangeStatus>() // 网络状态
  const userCloudTime = getCloudInfoHS.SuccessResult // 用户剩余时长
  const [processInfo, setProcessInfo] = useState<{ step: number; stepText: string }>()
  const [processStep, setProcessStep] = useState<number>(0)
  const [startSuccess, setStartSuccess] = useState<boolean>(false)
  const [fullScreenStatus, setFullScreenState] = useState<0 | 1>(0)
  /** 房主控制权 */
  const [role, setRole] = useState<'player' | 'viewer'>('player')
  const isHost = query.userType === 'host'
  // const userId = query.userId
  const isHang = query.isHang === '1'

  const startProcessAnimation = (start: number, end: number) => {
    if (animationId) {
      cancelAnimationFrame(animationId)
    }
    const animation = () => {
      start += 0.03
      const step = start >= end ? end : start
      setProcessStep(step)
      if (step < end) {
        animationId = requestAnimationFrame(animation)
      } else {
        animationId && cancelAnimationFrame(animationId)
      }
    }
    animationId = requestAnimationFrame(animation)
  }

  const startCloudInterval = () => {
    getCloudInfoHS.Execute()
    clearCloudInterval()
    timer = window.setInterval(() => {
      getCloudInfoHS.Execute()
    }, 1000 * 60 * 5)
  }

  const clearCloudInterval = () => {
    if (timer) {
      window.clearInterval(timer)
      timer = null
    }
  }

  const onConfigChange = (config: Config) => {
    setConfig(config)
    applyConfig(config)
  }

  /** 保存配置 */
  const applyConfig = (config: Config) => {
    const {
      screenConfig: { width, height },
      fpsConfig,
    } = config
    TCGSDK.setStreamProfile({ fps: +fpsConfig, max_bitrate: 15, min_bitrate: 4 })
    TCGSDK.setRemoteDesktopResolution({ width: +width, height: +height }).then(({ code }) => {
      console.log('setRemoteDesktopResolution ==> ', code)
    })
  }

  /** 初始化SDK */
  const init = async (params: Record<string, string>) => {
    TCGSDK.init({
      mount: 'mount-point',
      reconnect: true,
      fullVideoToScreen: true,
      showLoading: false,
      appid: +params.appId,
      showLogo: false,
      debugSetting: {
        showLog: true,
      },
      onConfigurationChange(change) {
        console.log(change)
      },
      onConnectSuccess,
      onConnectFail,
      onNetworkChange,
      onInitSuccess,
      onMultiPlayerChange,
      onDisconnect,
      onCursorShowStatChange({ oldStatus, newStatus }) {
        console.log({ oldStatus, newStatus })
      },
    })
  }

  /** 多人状态变更 */
  const onMultiPlayerChange = (response: OnMultiPlayerChangeResponse) => {
    /**
     *
     * user_state  用户 在线/离线
     * seats_info  房间坐席
     * submit_seat_change 申请切换控制权
     *
     */
    console.log(response, 'response')
    if (response.seats_info) {
      TCGSDK.getSeats().then(response => {
        console.log('seatsInfo', JSON.stringify(response))
        CloudGamePagePostMessage.post({ code: 'sync-seats-info', data: { seatsInfo: response } })
        const { players = [], viewers = [] } = response
        if (viewers.concat(players).length < 2 && !players.length) {
          seatChange({ to_role: 'player', user_id: query.gameCloudOrderNo })
        } else {
          const isViewers = viewers.some(player => player.name === query.gameCloudOrderNo)
          setRole(isViewers ? 'viewer' : 'player')
        }
      })
    }
    /** 申请切换控制权 */
    if (response.submit_seat_change) {
      const { user_id: submitUserId, to_role: role, seat_index: index } = response.submit_seat_change
      /** 修改房主控制权之后再确认 */
      const hostRole = role === 'player' ? 'viewer' : 'player'
      seatChange({ to_role: hostRole, user_id: query.gameCloudOrderNo }).then(() => {
        seatChange({ to_role: role, user_id: submitUserId })
      })
    }
  }

  /** 断开/被踢触发此回调，调用 start 接口成功后才会触发 */
  const onDisconnect = (e: OnDisconnectResponse) => {
    if (e.code === 0) {
      console.log('onDisconnect', e)
      handleExitGame(CLOUD_GAME_EXIT_TYPE.SDK_DISCONNECT)
    }
  }

  /** 连接失败回调，调用 start 接口成功后才会触发 */
  const onConnectFail = () => {
    console.log('onConnectFail')
  }

  /** 连接成功回调，调用 start 接口成功后才会触发 */
  const onConnectSuccess = () => {
    DWApplyCloudGameReport.Execute({
      gameCloudOrderNo: query?.gameCloudOrderNo || '',
      gameId: +(query?.platformGameId || 0),
      eventType: 'cloud_game_start_listener',
      msgContent: '',
    })
    applyConfig(config)
  }

  /** 网络状态变化 */
  const onNetworkChange = ({ status, stats }: OnNetworkChangeResponse) => {
    if (status === 'stats') {
      setNetWorkStatus(stats)
    }
  }

  /** 初始化完毕的回调，触发此回调之后才能调用后面的 API */
  const onInitSuccess = async () => {
    applyConfig(config)
    if (!isHang) {
      creatSession()
    } else {
      new Promise(resolve => {
        hangSuccess = resolve
        setTimeout(() => {
          runHangSuccess()
        }, 20000)
      }).then(creatSession)
    }
  }

  /** 挂盘完成 */
  const runHangSuccess = () => {
    hangSuccess?.()
    hangSuccess = null
  }

  /** 重新控制 */
  const onReConnectControl = () => {
    CloudGamePagePostMessage.post({ code: 'recover-control-req' })
  }

  /** 房主重新拿回控制权 */
  const forceControl = () => {
    return TCGSDK.getSeats().then(({ players = [] }) => {
      return Promise.all(
        players
          .filter(player => player.name !== query.gameCloudOrderNo)
          .map(player => seatChange({ to_role: 'viewer', user_id: player.name })),
      ).then(() => {
        seatChange({ to_role: 'player', user_id: query.gameCloudOrderNo })
      })
    })
  }

  /** 切换房间用户角色 */
  const seatChange = async (info: SeatChangeInfo) => {
    console.log('seatChange', JSON.stringify(info))
    const result = await TCGSDK.seatChange(info)
    if (result.code !== 0) {
      /** 切换控制失败 */
      console.warn(result)
      return Promise.reject(result)
    }
  }

  /** mini端启动参数 */
  const getLaunchParams = () => {
    const { startGameNo, cloudArchiveOrder, cloudOssPath = '' } = query || {}
    return btoa(
      JSON.stringify({
        startGameNo,
        gameCloudOrderNo: cloudArchiveOrder,
        cloudOssPath: unescape(encodeURIComponent(cloudOssPath)),
        env,
        ProdType: 'box',
        cloud_game: '1',
      }),
    )
  }

  /** 建立会话连接 */
  const creatSession = async () => {
    try {
      if (query) {
        const result = await DWApplyCloudGameConnect.Execute({
          gameCloudOrderNo: query.gameCloudOrderNo,
          gameId: +query.platformGameId,
          clientSession: TCGSDK.getClientSession(),
          startGameNo: query.startGameNo,
          launchParam: getLaunchParams(),
          linkUid: query.linkUid,
        })
        result && TCGSDK.start(result.serverSession)
      }
    } catch (error) {
      handleExitGame(CLOUD_GAME_EXIT_TYPE.CONNECT_SESSION_CREATE_FAILED)
      console.error(error)
    }
  }

  /** 退出游戏 */
  const handleExitGame = (reason: { code: number; message: string; reason: string }) => {
    TCGSDK.destroy({ code: reason.code, message: reason.reason })
    CloudGamePagePostMessage.post({ code: 'hide-cloud-game-view', data: reason })
  }

  /** 点击退出云游戏按钮 */
  const onExitGameClick = () => {
    CloudGamePagePostMessage.post({ code: 'request-exit' })
  }

  /** 切换全屏 */
  const onChangeScreen = () => {
    CloudGamePagePostMessage.post({ code: 'set_full_screen' })
  }

  /** 邀请玩家 */
  const onInvitePlayers = () => {
    CloudGamePagePostMessage.post({ code: 'invite-player' })
  }

  const onVideoBoxClick = () => {
    setControlCollapse(true)
  }

  const onConfigSet = (disable: boolean) => {
    if (!disable) {
      setVisibleConfigModal(true)
    }
  }

  /** 同步控制权信息 */
  useEffect(() => {
    CloudGamePagePostMessage.post({ code: 'sync-control', data: { control: role } })
  }, [role])

  useEffect(() => {
    if (query?.Authorization) {
      localStorage.setItem('php-token', query.Authorization)
      startCloudInterval()
      init(query)
      return () => {
        clearCloudInterval()
      }
    }
  }, [query])

  useEffect(() => {
    if (startSuccess) {
      startCloudInterval()
    }
  }, [startSuccess])

  useEffect(() => {
    const startFinishedOrder = localStorage.getItem('start-finished-orderNo')
    if (startFinishedOrder && startFinishedOrder === query?.gameCloudOrderNo) {
      setStartSuccess(true)
    }

    /** 启动进度 */
    CloudGamePagePostMessage.subscribe('start-progress', ({ process }) => {
      setProcessInfo(process)
    })

    /** 启动成功 */
    CloudGamePagePostMessage.subscribe('start-finished', () => {
      const cloudGameOrderNo = query?.gameCloudOrderNo
      cloudGameOrderNo && localStorage.setItem('start-finished-orderNo', cloudGameOrderNo)
      setStartSuccess(true)
    })

    /** 确认退出游戏弹窗显示 */
    CloudGamePagePostMessage.subscribe('exit-modal-status', ({ data }) => {
      TCGSDK?.setMouseCanLock(data.status !== 'show')
      TCGSDK?.setCursorShowStat(data.status === 'show')
    })

    /** ESC键盘按键事件 */
    CloudGamePagePostMessage.subscribe('request-unlock-pointer', () => {
      setTimeout(() => {
        TCGSDK?.setMouseCanLock(false)
        if (!TCGSDK?.getCursorShowStat()) {
          TCGSDK?.setCursorShowStat(true)
        }
        setControlCollapse(false)
      }, 300)
    })

    /** 切换全屏 */
    CloudGamePagePostMessage.subscribe('full-screen-change', ({ data }) => {
      setFullScreenState(data.status)
    })

    /** 重新获取控制权 */
    CloudGamePagePostMessage.subscribe('recover-control-res', ({ data }) => {
      forceControl().then(() => {
        CloudGamePagePostMessage.post({ code: 'force-recover' })
      })
    })

    /** 设置控制权 */
    CloudGamePagePostMessage.subscribe('set-control', ({ data: { control } }) => {
      TCGSDK.getSeats().then(response => {
        console.log(JSON.stringify(response))
      })
      console.log('set-control', control, control === 'player')
      if (control === 'player') {
        forceControl()
      } else {
        seatChange({ to_role: control, user_id: query.gameCloudOrderNo })
      }
    })
  }, [])

  useEffect(() => {
    if (processInfo) {
      const { step } = processInfo
      if (step >= 3) {
        runHangSuccess()
      }
      const start = step * 20
      const end = step >= 5 ? 100 : Math.min((step + 1) * 20, 90)
      startProcessAnimation(start, end)
    }
  }, [processInfo])

  return (
    <>
      {!startSuccess && (
        <StyledGameStartMask>
          {processInfo && (
            <StyledProgress
              data-step-text={`云游戏启动中(${processInfo?.stepText})...`}
              data-step-value={(processStep | 0) + '%'}
              step={processStep}
            >
              <span className="progress-track"></span>
            </StyledProgress>
          )}
        </StyledGameStartMask>
      )}
      <StyledPlayCloudGame id="mount-point" onClick={onVideoBoxClick}></StyledPlayCloudGame>
      <WindowSettingsModal
        visible={visibleConfigModal}
        title="分辨率设置"
        width={393}
        singleButton
        okText="应用配置"
        defaultConfig={config}
        configChange={onConfigChange}
        onOk={() => setVisibleConfigModal(false)}
        onCancel={() => setVisibleConfigModal(false)}
      />
      <StyledRealTimeMessage className={`${controlCollapse ? 'hide' : ''}`}>
        <div className="cloud-time-box">
          <img width={26} src={TimeImg} alt="" />
          <TimeShow label="剩余云游戏时长" time={(userCloudTime?.overtime || 0) * 1000} />
        </div>
        {config.showDelay && (
          <p className="sz-14 clr-fff">
            <span className="text-span">延迟 : {networkStatus?.delay || 0}</span>
            <span className="text-span">帧率 : {networkStatus?.fps || 0}</span>
          </p>
        )}
      </StyledRealTimeMessage>
      <StyledConfigController className={classnames({ collapse: controlCollapse })}>
        <div className="collapse-rect" onClick={() => setControlCollapse(false)}>
          <img className="collapse-rect-bg" src={RectImg} />
          <p className="sz-14 collapse-text">
            长按
            <span>E</span>
            <span>S</span>
            <span>C</span>
            解锁鼠标
          </p>
        </div>
        <div className="not-collapse">
          <div className="arrow-box" onClick={() => setControlCollapse(true)}>
            <img className="arrow-icon" src={ArrowRight} alt="" />
          </div>
          <div className="config-box">
            <div
              className={classnames('config-item', { disable: role !== 'player' })}
              title={role !== 'player' ? '暂无控制权' : ''}
              onClick={() => onConfigSet(role !== 'player')}
            >
              <img className="config-icon" src={ScreenSetImg} alt="" />
              <p className="sz-12 clr-fff">画面设置</p>
            </div>
            <div className="config-item" onClick={onChangeScreen}>
              <img className="config-icon" src={fullScreenStatus === 1 ? ExitScreenFullImg : ScreenFullImg} alt="" />
              <p
                className="sz-12 clr-fff hot-key"
                data-key="ALT+CTRL+F"
                data-text={fullScreenStatus === 1 ? '窗口模式' : '全屏模式'}
              ></p>
            </div>
            <div className="config-item" onClick={onExitGameClick}>
              <img className="config-icon" src={ExitImg} alt="" />
              <p className="sz-12 clr-fff hot-key" data-key="ALT + Q" data-text="退出游戏"></p>
            </div>
            {isHost && (
              <div className="config-item" onClick={role === 'player' ? onInvitePlayers : onReConnectControl}>
                <img className="config-icon" src={role === 'player' ? InviteImg : RecoverImg} alt="" />
                <p className="sz-12 clr-fff">{role === 'player' ? '邀请游戏' : '重新控制'}</p>
              </div>
            )}
          </div>
        </div>
      </StyledConfigController>
    </>
  )
}

const StyledGameStartMask = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  background: url(https://files.zuhaohao.com/demon/haisha_game_cloud_backgroud.jpg?v=${Date.now()}) no-repeat right
    center / cover;
`

const StyledProgress = styled.div<{ step: number }>`
  width: 485px;
  height: 10px;
  border-radius: 5px;
  background-color: #171a1c;
  line-height: 1;
  position: absolute;
  left: 50%;
  bottom: 60px;
  transform: translateX(-50%);
  &:before {
    content: attr(data-step-text);
    position: absolute;
    width: 100%;
    color: #fff;
    top: -25px;
    left: 0;
    font-size: 16px;
  }
  &:after {
    content: attr(data-step-value);
    position: absolute;
    top: -25px;
    right: 0;
    font-size: 16px;
    color: #fff;
  }
  .progress-track {
    display: block;
    height: 10px;
    border-radius: 5px;
    background: linear-gradient(270deg, #ffdf56 0%, #fea647 100%);
    ${props => `width: ${props.step}%;`}
  }
`

const StyledPlayCloudGame = styled.div`
  color: #fff;
`

const StyledRealTimeMessage = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  text-align: right;
  padding: 0.15rem 0.2rem;
  opacity: 1;
  z-index: 0;
  &.hide {
    opacity: 0;
    z-index: -1;
  }
  .sz-14 {
    font-size: 0.14rem;
  }
  .clr-878787 {
    color: #878787;
  }
  .clr-fff {
    color: #fff;
  }

  .cloud-time-box {
    display: flex;
    align-items: center;
    margin-bottom: 0.1rem;
    img {
      width: 0.26rem;
    }
  }
  .text-span {
    margin-left: 0.12rem;
  }
`

const StyledConfigController = styled.div`
  font-family: Din;
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: #fff;
  display: flex;
  align-items: center;
  p {
    margin: 0;
    color: #fff;
    font-size: 0.12rem;
  }

  .clr-878787 {
    color: #878787;
  }
  .clr-fff {
    color: #fff;
  }

  .not-collapse {
    width: 0.9rem;
    opacity: 1;
    display: flex;
    overflow: hidden;
    align-items: center;
    transition: all 0.3s linear;
    .arrow-box {
      width: 0.24rem;
      height: 0.8rem;
      cursor: pointer;
      font-size: 0;
      position: relative;
      background: rgba(18, 18, 18, 0.7);
      clip-path: polygon(0.02rem 0.1rem, 100% 0, 100% 100%, 0.02rem 0.7rem);
      .arrow-icon {
        width: 0.08rem;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
      }
    }
    .config-box {
      flex: 1;
      min-width: 0.66rem;
      min-height: 1.8rem;
      border-radius: 0.04rem;
      background: rgba(18, 18, 18, 0.7);
      padding: 0 0.06rem;
      .config-item {
        text-align: center;
        line-height: 2em;
        cursor: pointer;
        padding-top: 0.1rem;
        &.disable {
          cursor: not-allowed;
          opacity: 0.5;
        }
        p {
          width: 0.74rem;
          margin-left: -0.1rem;
          text-align: center;
          height: 0.32rem;
          overflow: hidden;
        }
        .config-icon {
          max-height: 0.22rem;
        }
        p.hot-key:before {
          content: attr(data-text);
          display: block;
          font-size: 0.1rem;
        }
        &:hover {
          .config-icon {
            animation: jump 1s ease-in-out;
          }
          .hot-key {
            &:before {
              transform: scale(0.8);
              content: attr(data-key);
            }
          }
        }
      }
      .config-item:not(:last-child) {
        border-bottom: 0.02rem solid rgba(74, 241, 204, 0.2);
      }
      @keyframes jump {
        0% {
          transform: translate(0);
        }
        10% {
          transform: translateY(0.05rem) scaleX(1.2) scaleY(0.8);
        }
        30% {
          transform: translateY(-0.13rem) scaleX(1) scaleY(1) rotate(5deg);
        }
        50% {
          transform: translateY(0) scale(1) rotate(0);
        }
        55% {
          transform: translateY(0) scaleX(1.1) scaleY(0.9) rotate(0);
        }
        70% {
          transform: translateY(-0.04rem) scaleX(1) scaleY(1) rotate(-2deg);
        }
        80% {
          transform: translateY(0) scaleX(1) scaleY(1) rotate(0);
        }
        85% {
          transform: translateY(0) scaleX(1.05) scaleY(0.95) rotate(0);
        }
        100% {
          transform: translateY(0) scaleX(1) scaleY(1);
        }
      }
    }
  }
  .collapse-rect {
    width: 0;
    opacity: 0;
    height: 1.95rem;
    overflow: hidden;
    cursor: pointer;
    position: relative;
    transition: all 0.3s linear;
    .collapse-rect-bg {
      height: 1.95rem;
    }
  }
  &.collapse {
    .collapse-rect {
      opacity: 1;
      width: 0.39rem;
      .collapse-text {
        position: absolute;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 18px;
        height: 100%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        line-height: 1.3em;
        padding: 0.1rem 0;
        span {
          display: block;
        }
        /* writing-mode: vertical-rl; */
      }
    }
    .not-collapse {
      width: 0;
      opacity: 0;
    }
  }
`

export default hot(observer(PlayCloudGame))
